import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import {
  CanvasItem,
  CanvasStreamFrame,
  ContextUploadFileItem,
  ImageBase64StreamFrame,
  ProgressMessageStreamFrame,
  SelectionWidgetStreamFrame,
  TextStreamFrame
} from '~/components/chat/message'
import type { ChatSelectionWidget } from '~/components/chat/message'
import { chatStream, CanvasContextInfo } from '~/services/chatService'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { BetterToast } from '~/utils/toast'

/** Todo item in a progress message checklist */
export interface ProgressMessageTodoItem {
  todo: string
  status: 'TODO_PENDING' | 'TODO_IN_PROGRESS' | 'TODO_COMPLETED'
}

export interface ProgressMessageData {
  taskStatus: string
  messageType: string
  title: string
  internalMsgs: string[]
  toolOutputs: string[]
  checkpointMsg: string
  msgHash: string
  createdTsNs: number
  // Client-only: monotonically increasing timestamp to represent "last update/arrival" for this msgHash.
  // This lets the UI style the latest progress message without changing chronological ordering.
  updatedTsNs?: number
  preview?: string // Preview snippet to show when collapsed
  todoList?: ProgressMessageTodoItem[] // For TODO_LIST message type
}

export interface StreamingTextDelta {
  index: number
  delta: string
  deltaLength: number
  accumulatedLength: number
  frameId?: number
  chunkId?: number
  chunkTotal?: number
  receivedAtMs: number
}

export interface StreamingMessage {
  id: string
  role: 'user' | 'assistant'
  type:
    | 'text'
    | 'task'
    | 'convo_title'
    | 'image_base64'
    | 'progress_message'
    | 'canvas'
    | 'coder_summary'
    | 'selection_widget'
  content: string
  textDeltas?: StreamingTextDelta[]
  continueStreamingAnimation?: boolean
  taskId?: number
  taskTitle?: string
  feedback?: 'positive' | 'negative'
  feedbackReason?: string
  otherReason?: string
  showFeedback?: boolean // Only show feedback for persisted messages, not streaming
  progressMessage?: ProgressMessageData // For progress_message type
  contextUpload?: ContextUploadFileItem[] // File/folder uploads attached to this message
  canvasItems?: CanvasItem[] // For canvas type - array of generated files/artifacts
  selectionWidget?: ChatSelectionWidget // For selection_widget type
  userName?: string
  createdTime?: string
}

interface ConversationStreamState {
  messages: StreamingMessage[]
  isStreaming: boolean
  abortController: AbortController | null
}

interface GlobalStreamingContextValue {
  getConversationState: (uuid: string) => ConversationStreamState
  sendMessage: (conversationUuid: string, message: string) => Promise<void>
  // Internal: Used by hooks that have workspace context
  sendMessageInternal: (
    conversationUuid: string,
    message: string,
    workspaceUuid: string,
    executionMode?: string,
    verificationMode?: string,
    contextUpload?: ContextUploadFileItem[],
    canvasContextInfo?: CanvasContextInfo[],
    isSpeechInput?: boolean
  ) => Promise<void>
  // Trigger for editable canvas refetch - set when SSE arrives for editable canvas
  canvasRefetchTrigger: { s3FilePath: string; timestamp: number } | null
}

const GlobalStreamingContext = createContext<GlobalStreamingContextValue | null>(null)

const DEFAULT_CONVERSATION_STATE: ConversationStreamState = {
  messages: [],
  isStreaming: false,
  abortController: null
}

export const GlobalStreamingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [conversationStates, setConversationStates] = useState<
    Record<string, ConversationStreamState>
  >({})

  // Trigger for editable canvas refetch - set when SSE arrives for editable canvas
  const [canvasRefetchTrigger, setCanvasRefetchTrigger] = useState<{
    s3FilePath: string
    timestamp: number
  } | null>(null)

  // Synchronous lock to prevent double sends. React state updates are async (batched),
  // so two rapid calls to sendMessageInternal can both see isStreaming=false. This ref
  // is set immediately before any async work, blocking the second call.
  const streamingLockRef = useRef<Set<string>>(new Set())

  // During streaming, backend progress frames may omit/zero `createdTsNs`.
  // We assign a stable per-msgHash timestamp on first sight so chronological ordering is stable
  // across updates/collisions (same msgHash arriving multiple times).
  const msgHashCreatedTsNsRef = useRef<Map<string, number>>(new Map())
  const msgHashCreatedSeqRef = useRef<number>(0)
  const msgHashUpdatedSeqRef = useRef<number>(0)

  const createTextMessage = useCallback(
    (id: string, role: 'user' | 'assistant', content: string): StreamingMessage => ({
      id,
      role,
      type: 'text',
      content: content || ''
    }),
    []
  )

  const createTaskMessage = useCallback(
    (id: string, taskId: number, taskTitle: string): StreamingMessage => ({
      id,
      role: 'assistant',
      type: 'task',
      content: '',
      taskId,
      taskTitle
    }),
    []
  )

  const createImageBase64Message = useCallback(
    (id: string, base64: string): StreamingMessage => ({
      id,
      role: 'assistant',
      type: 'image_base64',
      content: base64
    }),
    []
  )

  const createProgressMessage = useCallback(
    (id: string, progressData: ProgressMessageData): StreamingMessage => ({
      id,
      role: 'assistant',
      type: 'progress_message',
      content: progressData.title,
      progressMessage: progressData
    }),
    []
  )

  const createCanvasMessage = useCallback(
    (id: string, canvasItems: CanvasItem[]): StreamingMessage => ({
      id,
      role: 'assistant',
      type: 'canvas',
      content: '',
      canvasItems
    }),
    []
  )

  const createSelectionWidgetMessage = useCallback(
    (id: string, selectionWidget: ChatSelectionWidget): StreamingMessage => ({
      id,
      role: 'assistant',
      type: 'selection_widget',
      content: '',
      selectionWidget
    }),
    []
  )

  // Upsert progress message based on msgHash - deduplicates and maintains correct position
  const upsertProgressMessage = useCallback(
    (messages: StreamingMessage[], newMessage: StreamingMessage): StreamingMessage[] => {
      const msgHash = newMessage.progressMessage?.msgHash
      const messageType = newMessage.progressMessage?.messageType
      if (!msgHash || !messageType) {
        return [...messages, newMessage]
      }

      // Normalize createdTsNs to be stable per msgHash.
      // If the backend doesn't send createdTsNs during streaming, we fall back to a monotonic
      // client-side timestamp that preserves first-seen order.
      const ensuredCreatedTsNs = (() => {
        const existing = msgHashCreatedTsNsRef.current.get(msgHash)
        if (existing !== undefined) return existing

        const incoming = newMessage.progressMessage?.createdTsNs
        if (typeof incoming === 'number' && incoming > 0) {
          msgHashCreatedTsNsRef.current.set(msgHash, incoming)
          return incoming
        }

        // ms*1000 stays within JS safe integer range, and seq makes ties impossible.
        const local = Date.now() * 1000 + msgHashCreatedSeqRef.current++
        msgHashCreatedTsNsRef.current.set(msgHash, local)
        return local
      })()

      const normalizedNewMessage: StreamingMessage = {
        ...newMessage,
        progressMessage: {
          ...(newMessage.progressMessage as ProgressMessageData),
          createdTsNs: ensuredCreatedTsNs,
          // Always update "last seen" timestamp even when msgHash replaces in place.
          updatedTsNs: Date.now() * 1000 + msgHashUpdatedSeqRef.current++
        }
      }

      // If we already have a progress message with this hash, replace it IN PLACE.
      // This avoids "remove then re-insert" behavior which can reorder the item to the bottom,
      // and it preserves the original timestamp for stable chronological ordering.
      const existingIndex = messages.findIndex(
        msg => msg.type === 'progress_message' && msg.progressMessage?.msgHash === msgHash
      )

      // CUR-206: SUMMARY conceptually summarizes the CURRENT (latest) thinking
      // phase, not the whole turn. With interleaved turns (think -> text ->
      // think -> text -> SUMMARY) we MUST preserve the earlier-phase PMVs;
      // they're already-completed work the user wants to keep seeing. The old
      // behavior nuked every non-SUMMARY progress message in the array, which
      // erased the first phase's PMV the moment SUMMARY arrived.
      //
      // We define "current phase" as the contiguous suffix of the array
      // starting after the last text/canvas/image entry — those non-progress
      // entries delimit phases. Non-SUMMARY progress messages OUTSIDE that
      // suffix belong to an earlier phase and survive the SUMMARY collapse.
      const collapseCurrentPhaseForSummary = (list: StreamingMessage[]): StreamingMessage[] => {
        let phaseStart = 0
        for (let i = list.length - 1; i >= 0; i--) {
          if (list[i].type !== 'progress_message') {
            phaseStart = i + 1
            break
          }
        }
        if (phaseStart === 0) {
          // No prior-phase delimiter — original behavior: nuke ALL non-SUMMARY
          // progress messages (single-phase turn collapse).
          return list.filter(
            msg => msg.type !== 'progress_message' || msg.progressMessage?.messageType === 'SUMMARY'
          )
        }
        const head = list.slice(0, phaseStart)
        const tail = list
          .slice(phaseStart)
          .filter(
            msg => msg.type !== 'progress_message' || msg.progressMessage?.messageType === 'SUMMARY'
          )
        return [...head, ...tail]
      }

      if (existingIndex >= 0) {
        const replaced = [
          ...messages.slice(0, existingIndex),
          normalizedNewMessage,
          ...messages.slice(existingIndex + 1)
        ]

        if (messageType === 'SUMMARY') {
          return collapseCurrentPhaseForSummary(replaced)
        }

        return replaced
      }

      const filtered =
        messageType === 'SUMMARY' ? collapseCurrentPhaseForSummary(messages) : messages

      return [...filtered, normalizedNewMessage]
    },
    []
  )

  const getConversationState = useCallback(
    (uuid: string): ConversationStreamState => {
      return conversationStates[uuid] || DEFAULT_CONVERSATION_STATE
    },
    [conversationStates]
  )

  const updateConversationState = useCallback(
    (uuid: string, updates: Partial<ConversationStreamState>) => {
      setConversationStates(prev => ({
        ...prev,
        [uuid]: { ...prev[uuid], ...updates }
      }))
    },
    []
  )

  const clearConversationState = useCallback((uuid: string) => {
    setConversationStates(prev => {
      delete prev[uuid]
      return { ...prev }
    })
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sendMessage = useCallback(async (conversationUuid: string, message: string) => {
    // Note: This context implementation is internal and not directly called by components.
    // The hook layer (useStreamingChat) provides workspaceUuid from URL context.
    // This is a simplified interface for the public API.
    return
  }, [])

  // Internal implementation that accepts workspaceUuid (used by hooks)
  const sendMessageInternal = useCallback(
    async (
      conversationUuid: string,
      message: string,
      workspaceUuid: string,
      executionMode?: string,
      verificationMode?: string,
      contextUpload?: ContextUploadFileItem[],
      canvasContextInfo?: CanvasContextInfo[],
      isSpeechInput?: boolean
    ) => {
      // Synchronous ref check: prevents double sends when two calls happen in
      // the same React tick (e.g. rapid Enter key presses / double-click).
      if (streamingLockRef.current.has(conversationUuid)) {
        return
      }

      const currentState = getConversationState(conversationUuid)

      if (currentState.isStreaming) {
        return
      }

      // Acquire synchronous lock before any async work
      streamingLockRef.current.add(conversationUuid)

      if (currentState.abortController) {
        currentState.abortController.abort()
      }

      const abortController = new AbortController()
      let streamMessageSeq = 0
      const nextStreamMessageId = (prefix: string) =>
        `${prefix}-${Date.now()}-${streamMessageSeq++}`

      const userEchoMessage: StreamingMessage = {
        id: nextStreamMessageId('user-stream'),
        role: 'user',
        type: 'text',
        content: message,
        createdTime: new Date().toISOString()
      }
      const preservedMessages = currentState.messages.filter(
        message => message.type !== 'progress_message'
      )
      let tempMessages: StreamingMessage[] = [...preservedMessages, userEchoMessage]

      updateConversationState(conversationUuid, {
        messages: tempMessages,
        isStreaming: true,
        abortController
      })
      globalEventEmitter.emit(AppEvents.SendMessage, { conversationUuid })

      // New streaming run: reset per-hash timestamp assignment
      msgHashCreatedTsNsRef.current.clear()
      msgHashCreatedSeqRef.current = 0
      msgHashUpdatedSeqRef.current = 0

      let currentTextMessage: StreamingMessage | null = null
      let textContent = ''
      let textDeltas: StreamingTextDelta[] = []
      let currentCanvasMessage: StreamingMessage | null = null
      let canvasItems: CanvasItem[] = []
      // CUR-206: phase counter. The agent reuses the SAME msg_hash for its
      // "current thinking" PMV across multiple think -> text -> think phases
      // within one turn. Without phase awareness, upsertProgressMessage would
      // overwrite the prior phase's PMV in place every time a new phase begins,
      // collapsing the whole turn down to a single visible PMV. We namespace
      // each phase by appending `__phase-N` to the hash, so identical hashes
      // across phases become distinct entries in tempMessages while same-phase
      // updates still dedupe correctly.
      let phaseCounter = 0

      try {
        const stream = chatStream({
          workspaceUuid,
          conversationUuid,
          message,
          executionMode,
          verificationMode,
          contextUpload,
          canvasContextInfo,
          isSpeechInput,
          signal: abortController.signal
        })

        for await (const chunk of stream) {
          if (abortController.signal.aborted) {
            return
          }

          switch (chunk.event) {
            case 'task': {
              const taskData = chunk.data as { task_id: number; title: string }
              const taskMessage = createTaskMessage(
                `task-${taskData.task_id}`, // Stable ID for deduplication
                taskData.task_id,
                taskData.title
              )

              // Check if this task already exists (by taskId)
              const existingTaskIndex = tempMessages.findIndex(
                msg => msg.type === 'task' && msg.taskId === taskData.task_id
              )

              if (existingTaskIndex >= 0) {
                // Update existing task in place (preserves position)
                tempMessages = [
                  ...tempMessages.slice(0, existingTaskIndex),
                  taskMessage,
                  ...tempMessages.slice(existingTaskIndex + 1)
                ]
              } else {
                // New task - append at end
                tempMessages = [...tempMessages, taskMessage]
              }
              break
            }

            case 'image_base64': {
              const imageBase64 = (chunk as ImageBase64StreamFrame).data.base64
              const imageMessage = createImageBase64Message(
                nextStreamMessageId('image_base64'),
                imageBase64
              )
              tempMessages = [...tempMessages, imageMessage]
              break
            }

            case 'progress_message': {
              const progressData = (chunk as ProgressMessageStreamFrame).data
              // CUR-206: when a progress_message event arrives while an
              // assistant text segment is actively being appended, that signals
              // the agent paused emitting text to "think again" — the next
              // text delta should start a NEW assistant-text message, not
              // continue the prior one. This is the live mirror of the backend's
              // _save_messages_in_order arrival-order persistence.
              //
              // Phase-break detection mode (default 'any'):
              //   - 'any':  any progress event during an active text segment
              //             starts a new segment. Defensive against agents that
              //             reuse hashes across phase breaks.
              //   - 'new-hash': only progress events with a never-before-seen
              //             msgHash start a new segment. Stricter — trusts the
              //             agent to issue a fresh hash per phase.
              // Override via localStorage.chat_debug_phase_boundary = 'new-hash'.
              const phaseBoundaryMode =
                typeof window !== 'undefined' &&
                window.localStorage?.getItem('chat_debug_phase_boundary') === 'new-hash'
                  ? 'new-hash'
                  : 'any'
              const isNewProgressHash =
                !!progressData.msgHash &&
                !tempMessages.some(
                  msg =>
                    msg.type === 'progress_message' &&
                    msg.progressMessage?.msgHash === progressData.msgHash
                )
              const shouldStartNewTextSegment =
                !!currentTextMessage && (phaseBoundaryMode === 'any' || isNewProgressHash)
              if (shouldStartNewTextSegment) {
                currentTextMessage = null
                textContent = ''
                textDeltas = []
                // CUR-206: a phase boundary just occurred. Bump the phase
                // counter so future progress events with hashes reused from a
                // prior phase get a fresh effective hash and render as new
                // PMVs instead of clobbering the prior phase's PMV in place.
                phaseCounter += 1
              }
              // CUR-206: namespace the hash by phase. Same-phase updates with
              // the same original hash still dedupe (the suffix is identical).
              // SUMMARY messages are NOT namespaced — they represent the whole
              // turn and need to retain their identity across phases so the
              // existing SUMMARY-takes-over logic (and post-stream "Worked for
              // Xs" rendering) still resolves them as one entry.
              const isSummary = progressData.messageType === 'SUMMARY'
              const effectiveHash =
                isSummary || !progressData.msgHash || phaseCounter === 0
                  ? progressData.msgHash
                  : `${progressData.msgHash}__phase-${phaseCounter}`
              const effectiveProgressData =
                effectiveHash === progressData.msgHash
                  ? progressData
                  : { ...progressData, msgHash: effectiveHash }
              const progressMessage = createProgressMessage(
                `progress-${effectiveHash}`,
                effectiveProgressData
              )
              tempMessages = upsertProgressMessage(tempMessages, progressMessage)
              break
            }

            case 'canvas': {
              const canvasData = (chunk as CanvasStreamFrame).data
              if (canvasData.items && canvasData.items.length > 0) {
                // Accumulate all canvas items
                canvasItems = [...canvasItems, ...canvasData.items]
                // Trigger refetch for editable canvas items
                for (const item of canvasData.items) {
                  if (item.editable && item.s3FilePath) {
                    setCanvasRefetchTrigger({ s3FilePath: item.s3FilePath, timestamp: Date.now() })
                  }
                }

                if (!currentCanvasMessage) {
                  currentCanvasMessage = createCanvasMessage(
                    nextStreamMessageId('canvas'),
                    canvasItems
                  )
                  tempMessages = [...tempMessages, currentCanvasMessage]
                } else {
                  tempMessages = tempMessages.map(msg =>
                    msg.id === currentCanvasMessage!.id
                      ? { ...msg, canvasItems: [...canvasItems] }
                      : msg
                  )
                }
              }
              break
            }

            case 'selection_widget': {
              const selectionData = (chunk as SelectionWidgetStreamFrame).data
              if (selectionData.widget) {
                tempMessages = [
                  ...tempMessages,
                  createSelectionWidgetMessage(
                    nextStreamMessageId('selection-widget'),
                    selectionData.widget
                  )
                ]
              }
              break
            }

            case 'text': {
              const textChunk = chunk as TextStreamFrame
              const delta = textChunk.data.delta
              textContent += delta
              textDeltas = [
                ...textDeltas,
                {
                  index: textDeltas.length,
                  delta,
                  deltaLength: delta.length,
                  accumulatedLength: textContent.length,
                  frameId: textChunk.frame_id,
                  chunkId: textChunk.chunk_id,
                  chunkTotal: textChunk.chunk_total,
                  receivedAtMs: Date.now()
                }
              ]

              if (!currentTextMessage) {
                currentTextMessage = createTextMessage(
                  nextStreamMessageId('assistant-text'),
                  'assistant',
                  textContent
                )
                currentTextMessage.textDeltas = textDeltas
                tempMessages = [...tempMessages, currentTextMessage]
              } else {
                tempMessages = tempMessages.map(msg =>
                  msg.id === currentTextMessage!.id
                    ? { ...msg, content: textContent, textDeltas }
                    : msg
                )
              }
              break
            }

            case 'convo_title': {
              break
            }

            case 'end': {
              streamingLockRef.current.delete(conversationUuid)
              updateConversationState(conversationUuid, {
                isStreaming: false,
                abortController: null
              })
              return
            }
          }

          updateConversationState(conversationUuid, {
            messages: [...tempMessages]
          })
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          updateConversationState(conversationUuid, {
            messages: [...tempMessages],
            isStreaming: false,
            abortController: null
          })
          return
        }

        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        const errorType = (error as Error & { errorType?: string }).errorType

        // 409 Conflict: conversation already has an active request (e.g. after page refresh)
        if (errorType === 'processing') {
          BetterToast.error(
            'This conversation is still processing a previous request. Please wait.'
          )
        } else if (errorType) {
          // Other typed errors: emit event for ModalContext to handle (shows AlertModal)
          globalEventEmitter.emit(AppEvents.ApiForbiddenError, {
            errorType,
            message: errorMessage,
            url: 'chat'
          })
        } else {
          BetterToast.error(`Failed to send message: ${errorMessage}`)
        }
        clearConversationState(conversationUuid)
        // Re-throw so callers can handle (e.g., remove optimistic user message)
        throw error
      } finally {
        streamingLockRef.current.delete(conversationUuid)

        // Server handles connection cleanup - just clean up client state
        if (abortController && !abortController.signal.aborted) {
          abortController.abort()
        }

        updateConversationState(conversationUuid, {
          isStreaming: false,
          abortController: null
        })
      }
    },
    [
      getConversationState,
      updateConversationState,
      createTextMessage,
      createTaskMessage,
      createProgressMessage,
      upsertProgressMessage,
      clearConversationState,
      createImageBase64Message,
      createCanvasMessage,
      createSelectionWidgetMessage
    ]
  )

  const contextValue = useMemo(
    (): GlobalStreamingContextValue => ({
      getConversationState,
      sendMessage,
      sendMessageInternal,
      canvasRefetchTrigger
    }),
    [getConversationState, sendMessage, sendMessageInternal, canvasRefetchTrigger]
  )

  return (
    <GlobalStreamingContext.Provider value={contextValue}>
      {children}
    </GlobalStreamingContext.Provider>
  )
}

export const useGlobalStreaming = (): GlobalStreamingContextValue => {
  const context = useContext(GlobalStreamingContext)
  if (!context) {
    throw new Error('useGlobalStreaming must be used within a GlobalStreamingProvider')
  }
  return context
}
